import { supabase } from './client';

export interface EmailSubscription {
  id: string;
  email: string;
  user_id: string | null;
  unsubscribe_token: string;
  subscribed_at: string;
  is_active: boolean;
  created_at: string;
}

export async function subscribeToJobAlerts(email: string, userId?: string) {
  const { data, error } = await supabase
    .from('email_subscriptions')
    .insert([
      {
        email,
        user_id: userId || null,
        is_active: true,
      },
    ])
    .select()
    .single();

  if (error) {
    if (error.code === '23505') {
      throw new Error('This email is already subscribed');
    }
    throw error;
  }

  try {
    const functionUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-welcome-email`;

    await fetch(functionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({
        email: data.email,
        unsubscribeToken: data.unsubscribe_token,
      }),
    });
  } catch (emailError) {
    console.error('Failed to send welcome email:', emailError);
  }

  return data;
}

export async function unsubscribeFromJobAlerts(token: string) {
  const { error } = await supabase
    .from('email_subscriptions')
    .update({ is_active: false })
    .eq('unsubscribe_token', token)
    .eq('is_active', true);

  if (error) throw error;
}

export async function getActiveSubscriptions() {
  const { data, error } = await supabase
    .from('email_subscriptions')
    .select('*')
    .eq('is_active', true);

  if (error) throw error;
  return data as EmailSubscription[];
}

export async function getUserSubscription(userId: string) {
  const { data, error } = await supabase
    .from('email_subscriptions')
    .select('*')
    .eq('user_id', userId)
    .eq('is_active', true)
    .maybeSingle();

  if (error) throw error;
  return data as EmailSubscription | null;
}

export async function updateStoredResume(userId: string, resumeUrl: string) {
  const { data: existingProfile } = await supabase
    .from('job_seekers')
    .select('id')
    .eq('user_id', userId)
    .maybeSingle();

  if (existingProfile) {
    const { data, error } = await supabase
      .from('job_seekers')
      .update({ resume_url: resumeUrl })
      .eq('user_id', userId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } else {
    const { data, error } = await supabase
      .from('job_seekers')
      .insert({
        user_id: userId,
        resume_url: resumeUrl,
        job_types: [],
        locations: [],
        skills: [],
        certifications: [],
        experience: '',
        availability: '',
        desired_pay: '',
        about: '',
        email: '',
        phone: ''
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  }
}

export async function getStoredResume(userId: string) {
  const { data, error } = await supabase
    .from('job_seekers')
    .select('resume_url')
    .eq('user_id', userId)
    .maybeSingle();

  if (error) throw error;
  return data?.resume_url || null;
}
